import {
  Box,
  Button,
  FieldWrapper,
  FlexBox,
  FormField,
  FormGrid,
  Icon,
  InputWrapper,
  Page,
} from '@dixa/lib-shared-habla';
import { Sentry } from 'lib-shared-sentry';
import { useMemo, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useDebounce, useUpdateEffect } from 'react-use';

import { billingApi, PaymentInformationPatchAttributes } from 'apis/billing';
import { countries } from 'app/utils/countries/countries';
import { HookFormText } from 'components/hookForm';
import { useNotifications } from 'hooks/useNotifications';

import { PaymentInformationFormDefaultValues } from './billingInformation.types';
import styles from './billingInformationSection.module.scss';
import { BillingInformation } from './components/billingInformation';

const formId = 'billing-information-form';

export const BillingInformationSection = (): JSX.Element => {
  const notify = useNotifications();
  const [isEditable, setIsEditable] = useState(false);
  const { paymentInformation, isFetching: isPaymentInformationFetching } =
    billingApi.useReadPaymentInformationQuery(undefined, {
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        paymentInformation: data?.data.payment_information,
      }),
    });

  const defaultValues = useMemo(() => {
    if (!paymentInformation) return;

    const keys: (keyof PaymentInformationFormDefaultValues)[] = [
      'name',
      'company_name',
      'country',
      'state',
      'email',
      'address1',
      'address2',
      'postal_code',
      'city',
      'vatin',
    ];

    return keys.reduce(
      (acc, key) => ({ ...acc, [key]: paymentInformation[key] ?? '' }),
      {} as PaymentInformationFormDefaultValues,
    );
  }, [paymentInformation]);

  const [
    updatePaymentInformation,
    { isLoading: isPaymentInformationUpdating },
  ] = billingApi.useUpdatePaymentInformationMutation();

  const [readValidateVatNumber] =
    billingApi.useLazyReadValidateVatNumberQuery();

  const isPaymentInformationFormDisabled =
    isPaymentInformationFetching || isPaymentInformationUpdating;

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, touchedFields },
    register,
    setError,
    clearErrors,
    setFocus,
    watch,
  } = useForm({
    mode: 'all',
    defaultValues,
  });

  const watchedVatin = watch('vatin');

  const validateVatinFormField = async (): Promise<void> => {
    if (!touchedFields.vatin) return;

    try {
      const { data } = await readValidateVatNumber({
        vatNumber: watchedVatin,
      }).unwrap();

      if (!data.result) {
        return setError('vatin', {
          message:
            'The VAT ID number is invalid, please use the format DK99999999',
          type: 'value',
        });
      }

      return clearErrors('vatin');
    } catch (error: unknown) {
      Sentry.captureException(error);
    }
  };

  const [_, cancelDebouncedValidateVatinFormField] = useDebounce(
    validateVatinFormField,
    300,
    [watchedVatin, touchedFields.vatin],
  );

  useUpdateEffect(() => {
    cancelDebouncedValidateVatinFormField();
    reset(defaultValues);
  }, [defaultValues, reset]);

  const handleOnSubmit = async (values: FieldValues) => {
    const { updates, deletes } = Object.keys(values).reduce(
      (acc, key) => {
        if (values[key] === '') {
          acc.deletes.push(key as PaymentInformationPatchAttributes);
        } else {
          acc.updates[key] = values[key];
        }

        return acc;
      },
      { updates: {}, deletes: [] } as {
        updates: Record<string, string | null>;
        deletes: PaymentInformationPatchAttributes[];
      },
    );

    try {
      const { data } = await updatePaymentInformation({
        patch: {
          updates,
          deletes,
        },
      }).unwrap();

      if (Object.keys(data.update_errors).length) {
        throw data.update_errors;
      }

      reset(values);
      setIsEditable(false);

      notify({
        title: 'Successfully updated billing information',
        icon: 'done',
        className: 'success',
        time: 3000,
        showDesktop: false,
      });
    } catch (error: unknown) {
      const errorMessage = (error as any).InvalidType
        ? (error as any).InvalidType
        : `Please try again or contact support.`;

      if (errorMessage.includes('VATIN')) {
        setFocus('vatin');
        validateVatinFormField();
      }

      notify({
        title: 'Failed to update update billing information',
        text: errorMessage,
        icon: 'exclamation',
        className: 'danger',
        time: 5000,
        showDesktop: false,
      });
    }
  };

  return (
    <Page.Section
      title="Billing information"
      controls={
        <>
          {isEditable && (
            <>
              <Button
                variant="secondary"
                onClick={() => {
                  reset(defaultValues);
                  setIsEditable(false);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                form={formId}
                isLoading={isPaymentInformationUpdating}
              >
                Save details
              </Button>
            </>
          )}
          {!isEditable && (
            <Button variant="secondary" onClick={() => setIsEditable(true)}>
              Edit
            </Button>
          )}
        </>
      }
    >
      <Box className={styles.paymentInformation} minHeight="170px">
        {!isEditable && (
          <BillingInformation
            name={paymentInformation?.name}
            address1={paymentInformation?.address1}
            address2={paymentInformation?.address2 ?? ''}
            city={paymentInformation?.city}
            companyName={paymentInformation?.company_name}
            country={paymentInformation?.country}
            email={paymentInformation?.email}
            postalCode={paymentInformation?.postal_code}
            state={paymentInformation?.state}
            vatin={paymentInformation?.vatin}
            isLoading={isPaymentInformationFetching}
          />
        )}

        {isEditable && (
          <Box padding="300 0 0 0">
            <form
              id={formId}
              onSubmit={handleSubmit(handleOnSubmit)}
              noValidate
            >
              <FormGrid>
                <FormField span={3}>
                  <HookFormText
                    name="name"
                    label="Name"
                    control={control}
                    disabled={isPaymentInformationFormDisabled}
                    rules={{
                      required: 'Name is required',
                    }}
                  />
                </FormField>
                <FormField span={3}>
                  <HookFormText
                    name="email"
                    label="Email"
                    control={control}
                    disabled={isPaymentInformationFormDisabled}
                    helpIcon={{
                      text: 'The email address to receive invoices',
                    }}
                    rules={{
                      required: 'Billing email is required',
                      pattern: {
                        value: /\S+@\S+\.\S+/,
                        message: 'Entered value does not match email format',
                      },
                    }}
                  />
                </FormField>
                <FormField span={3}>
                  <HookFormText
                    name="company_name"
                    label="Company name"
                    control={control}
                    disabled={isPaymentInformationFormDisabled}
                    rules={{
                      required: 'Company name is required',
                    }}
                  />
                </FormField>
                <FormField span={3}>
                  <HookFormText
                    name="vatin"
                    label="VAT ID number"
                    control={control}
                    disabled={isPaymentInformationFormDisabled}
                    helpIcon={{
                      text: 'VAT ID number is used in many countries for tax purposes. Please provide your VAT ID number if you are purchasing Dixa for a business purpose and is EU based.',
                    }}
                    componentProps={{
                      placeholder: 'VAT ID number... (eg. DK99999999)',
                    }}
                  />
                </FormField>
                <FormField span={3}>
                  <HookFormText
                    name="address1"
                    label="Address (P.O. box, company name, c/o) "
                    control={control}
                    disabled={isPaymentInformationFormDisabled}
                    rules={{
                      required: 'Address is required',
                    }}
                  />
                </FormField>
                <FormField span={3}>
                  <HookFormText
                    name="address2"
                    label="Address line 2 (Apartment, suite, unit)"
                    control={control}
                    disabled={isPaymentInformationFormDisabled}
                  />
                </FormField>
                <FormField span={3}>
                  <HookFormText
                    name="city"
                    label="City"
                    control={control}
                    disabled={isPaymentInformationFormDisabled}
                    rules={{
                      required: 'City is required',
                    }}
                  />
                </FormField>
                <FormField span={3}>
                  <HookFormText
                    name="postal_code"
                    label="Postal/Zip code"
                    control={control}
                    disabled={isPaymentInformationFormDisabled}
                    rules={{
                      required: 'Zip code is required',
                    }}
                  />
                </FormField>
                <FormField span={3}>
                  <FieldWrapper
                    label="Country"
                    required={true}
                    disabled={isPaymentInformationFormDisabled}
                    meta={{
                      error: errors.country?.message,
                      valid: !errors.country?.message,
                      touched: !!touchedFields.country,
                    }}
                  >
                    <InputWrapper className={styles.selectWrapper}>
                      <select
                        className={styles.select}
                        {...register('country', {
                          required: 'Country is required',
                          disabled: isPaymentInformationFormDisabled,
                        })}
                      >
                        <option value="" defaultChecked>
                          Choose your country
                        </option>
                        {countries.map((item) => (
                          <option
                            key={item.countryCode}
                            value={item.countryCode}
                          >
                            {item.name}
                          </option>
                        ))}
                      </select>
                      <FlexBox
                        className={styles.selectWrapperIconWrapper}
                        width="24px"
                        height="24px"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Icon name="angle-down" color="tertiary" />
                      </FlexBox>
                    </InputWrapper>
                  </FieldWrapper>
                </FormField>
                <FormField span={3}>
                  <HookFormText
                    name="state"
                    label="State/Province"
                    control={control}
                    disabled={isPaymentInformationFormDisabled}
                  />
                </FormField>
              </FormGrid>
            </form>
          </Box>
        )}
      </Box>
    </Page.Section>
  );
};
